import { Fragment, memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

import { noop } from '@tanstack/react-table';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  CompactPagination,
  Divider,
  Grid,
  Pagination,
  Tooltip,
  useMediaQuery,
} from '@kc-console/common-ui';
import { t } from '@kc-console/core';
import {
  IcFilterOnRegular,
  IcFilterType2Regular,
  IcSettingOnRegular,
  IcSettingType2Regular,
} from '@kep/kcds-icons';

import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS, getIsFilterApplied } from '../DataTable';
import { RefetchButton } from '../RefetchButton';
import ActionBarActions from './ActionBarActions';
import ActionBarFilterContent from './ActionBarFilterContent';
import { actionBarClasses } from './utils';

import type { ChangeEvent, ReactNode } from 'react';
import type { VisibilityState } from '@tanstack/react-table';
import type { DividerProps, PropsWithClassName } from '@kc-console/common-ui';
import type { ResourceAction } from '../ActionButtonsGroup';
import type { DataTableColumnContent, FilterDefBase, FilterState } from '../DataTable/types';
import type { PropertyFilterType } from './utils';

export interface ActionBarProps extends PropsWithClassName {
  settings: {
    columns: DataTableColumnContent<unknown>[];
    onColumnOrderChange: (columnOrder: string[]) => void;
    onColumnVisibilityChange: (columnVisibility: VisibilityState) => void;
  };
  settingsContent: ReactNode;
  actions?: ResourceAction[];
  filter?: {
    filterDefs: FilterDefBase[];
    filters: FilterState[];
    onFiltersChange: (filter: FilterState[]) => void;
    autoFocusFilterButton?: boolean;
    autoFocusPropertyFilter?: boolean;
    /**
     * 필터가 있을 때 자동으로 필터를 열지 여부를 설정합니다.
     * @default true
     */
    autoOpenFilterWhenHasFilters?: boolean;
    /**
     * 필터의 초기 기준값입니다. 현재 필터가 이 값과 다를 때만 적용 아이콘, 자동 열림,
     * 초기화 버튼을 활성화하며 초기화 시 이 값으로 복원합니다.
     * 지정하지 않으면 빈 필터 목록을 기준값으로 사용합니다.
     */
    initialFilters?: FilterState[];
    propertyFilterType?: PropertyFilterType;
  };
  /**
   * 연결이 끊긴 상태를 나타냅니다.
   * true이고 isFetching이 아닐 때 refetch 버튼 우측 상단에 빨간 점을 표시합니다.
   */
  isDisconnected?: boolean;
  isFetching?: boolean;
  isSettingsChanged?: boolean;
  /**
   * 더보기 버튼에 표시할 라벨입니다. 미지정 시 기본 번역 키를 사용합니다.
   */
  moreButtonLabel?: string;
  onRefetch?: () => void;
  pagination?: {
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    defaultPageSize?: number;
    hasMore?: boolean;
    onPageIndexChange?: (pageIndex: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    pageSizeOptions?: number[];
    type?: 'auto' | 'numbers' | 'compact';
  };
  /**
   * refetch 버튼 툴팁 메시지입니다.
   * @default ''
   */
  refetchTooltip?: string;
  showFilter?: boolean;
  showPagination?: boolean;
  showSetting?: boolean;
  showSideMargin?: boolean;
  /**
   * 액션 버튼의 개수를 설정합니다. 설정하지 않았을 경우 자동으로 보정됩니다.
   *
   * @default undefined
   * 보정 조건
   * - 화면 너비가 sm 이하일 경우 1개
   * - 화면 너비가 md 이상일 경우 5개
   */
  visibleActionsCount?: number;
}

export default memo(function ActionBar(props: ActionBarProps) {
  const {
    actions = [],
    className,
    filter,
    showFilter = true,
    showPagination = true,
    showSetting = true,
    isFetching,
    isDisconnected,
    onRefetch,
    pagination,
    refetchTooltip = '',
    settings,
    settingsContent,
    isSettingsChanged,
    showSideMargin,
    moreButtonLabel,
    visibleActionsCount,
  } = props;

  const {
    filters = [],
    onFiltersChange,
    filterDefs = [],
    initialFilters,
    autoFocusFilterButton = true,
    autoFocusPropertyFilter = true,
    propertyFilterType,
    autoOpenFilterWhenHasFilters = true,
  } = filter ?? {};

  const isFilterApplied = getIsFilterApplied(filters, initialFilters, filterDefs);
  const filterContentId = useId();
  const settingContentId = useId();
  const filterButtonLabel = t('ui:action_bar.filter.label');
  const settingButtonLabel = t('ui:action_bar.settings.label');

  const [activeButton, setActiveButton] = useState<'FILTER' | 'SETTING' | null>(
    isFilterApplied && autoOpenFilterWhenHasFilters ? 'FILTER' : null,
  );

  const {
    pageIndex = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    totalCount = 0,
    defaultPageSize = DEFAULT_PAGE_SIZE,
    hasMore,
    onPageIndexChange,
    onPageSizeChange,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    type: paginationTypeProp = 'auto',
  } = pagination ?? {};

  const { columns, onColumnVisibilityChange, onColumnOrderChange } = settings ?? {};

  const filterButtonRef = useRef<HTMLButtonElement | null>(null);

  // Pagination 컴포넌트의 page는 0부터 시작하므로 1을 빼준다.
  const zeroBasedPageIndex = useMemo(() => pageIndex - 1, [pageIndex]);

  const screenGteMd = useMediaQuery(theme => theme.breakpoints.up('md'));
  const paginationType =
    paginationTypeProp === 'auto' ? (screenGteMd ? 'numbers' : 'compact') : paginationTypeProp;

  const handlePageIndexChange = useCallback(
    (e: ChangeEvent<unknown> | null, nextZeroBasedPageIndex: number) => {
      // Pagination 컴포넌트의 page는 0부터 시작하므로 1을 더해준다.
      onPageIndexChange?.(nextZeroBasedPageIndex + 1);
    },
    [onPageIndexChange],
  );

  useEffect(() => {
    if (autoFocusFilterButton && filterButtonRef.current) {
      // 필터 포커스가 아니거나 필터가 닫혀있을 때만 filter button에 포커스
      if (!autoFocusPropertyFilter || activeButton !== 'FILTER') {
        filterButtonRef.current.focus({ preventScroll: true });
      }
    }
  }, [autoFocusFilterButton, autoFocusPropertyFilter, activeButton]);

  const toolbarLeftContent = [
    showFilter && (
      <ActionBarButtonsButton
        className={actionBarClasses.toolbarButtonsFilterButton}
        aria-label={filterButtonLabel}
        aria-controls={activeButton === 'FILTER' ? filterContentId : undefined}
        aria-expanded={activeButton === 'FILTER'}
        data-active={activeButton === 'FILTER'}
        onClick={() => setActiveButton(activeButton === 'FILTER' ? null : 'FILTER')}
        ref={filterButtonRef}
      >
        {isFilterApplied ? <IcFilterOnRegular /> : <IcFilterType2Regular />}
        <ActionBarButtonsButtonLabel className={actionBarClasses.toolbarButtonsFilterButtonLabel}>
          {filterButtonLabel}
        </ActionBarButtonsButtonLabel>
      </ActionBarButtonsButton>
    ),
    actions.length > 0 && (
      <ActionBarActions
        actions={actions}
        moreButtonLabel={moreButtonLabel}
        visibleActionsCount={visibleActionsCount}
      />
    ),
    showSetting && (
      <ActionBarButtonsButton
        className={actionBarClasses.toolbarButtonsSettingButton}
        aria-label={settingButtonLabel}
        aria-controls={activeButton === 'SETTING' ? settingContentId : undefined}
        aria-expanded={activeButton === 'SETTING'}
        data-active={activeButton === 'SETTING'}
        onClick={() => setActiveButton(activeButton === 'SETTING' ? null : 'SETTING')}
      >
        {isSettingsChanged ? <IcSettingOnRegular /> : <IcSettingType2Regular />}
        <ActionBarButtonsButtonLabel className={actionBarClasses.toolbarButtonsSettingButtonLabel}>
          {settingButtonLabel}
        </ActionBarButtonsButtonLabel>
      </ActionBarButtonsButton>
    ),
  ]
    .filter(Boolean)
    .map((content, contentIndex) => (
      <Fragment key={contentIndex}>
        {contentIndex !== 0 && <ActionBarDivider />}
        {content}
      </Fragment>
    ));

  const toolbarRightContent = [
    onRefetch && (
      <Tooltip message={refetchTooltip} placement="bottom">
        <RefetchButtonContainer>
          <RefetchButton isFetching={isFetching} onClick={onRefetch} />
          {isDisconnected && !isFetching && <RefetchDisconnectedDot />}
        </RefetchButtonContainer>
      </Tooltip>
    ),
    showPagination &&
      (paginationType === 'numbers' ? (
        <Pagination
          page={zeroBasedPageIndex}
          rowsPerPage={pageSize}
          count={totalCount}
          hasMore={hasMore}
          onChange={handlePageIndexChange}
        />
      ) : (
        <CompactPagination
          page={zeroBasedPageIndex}
          rowsPerPage={pageSize}
          count={totalCount}
          hasMore={hasMore}
          onPageChange={handlePageIndexChange}
        />
      )),
  ]
    .filter(Boolean)
    .map((content, contentIndex) => (
      <Fragment key={contentIndex}>
        {contentIndex !== 0 && <ActionBarDivider />}
        {content}
      </Fragment>
    ));

  if (toolbarLeftContent.length === 0 && toolbarRightContent.length === 0) {
    return null;
  }
  return (
    <ActionBarRoot
      className={clsx(className, actionBarClasses.root)}
      showSideMargin={showSideMargin}
    >
      <ActionBarToolbar className={actionBarClasses.toolbar} container gap={'8px'}>
        <Grid
          className={actionBarClasses.toolbarButtons}
          container
          alignItems="center"
          flexGrow={1}
        >
          {toolbarLeftContent}
        </Grid>
        <ActionBarPaginationContainer
          className={actionBarClasses.toolbarPagination}
          container
          alignItems="center"
        >
          {toolbarRightContent}
        </ActionBarPaginationContainer>
      </ActionBarToolbar>
      {activeButton === 'FILTER' && (
        <ActionBarFilterContent
          id={filterContentId}
          filters={filters}
          onFiltersChange={onFiltersChange ?? noop}
          filterDefs={filterDefs}
          initialFilters={initialFilters}
          autoFocusPropertyFilter={autoFocusPropertyFilter}
          propertyFilterType={propertyFilterType}
        />
      )}
      {activeButton === 'SETTING' && (
        <Grid
          id={settingContentId}
          className={actionBarClasses.settingContent}
          container
          gap="8px"
          alignItems="center"
        >
          {settingsContent}
        </Grid>
      )}
    </ActionBarRoot>
  );
});

const ActionBarRoot = styled(Grid, {
  shouldForwardProp: prop => prop !== 'showSideMargin',
})<{ showSideMargin?: boolean }>(
  ({ theme, showSideMargin }) => css`
    position: relative;
    /* viewport가 아니라 ActionBar 자체 너비를 기준으로 Figma xs 변형을 적용한다. */
    container: action-bar / inline-size;
    padding: ${showSideMargin ? '0 32px 0 24px' : '0 16px 0 6px'};

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      border-top: ${theme.mixins.border('border/weak/default')};
    }

    & > .${actionBarClasses.filterContent}, & > .${actionBarClasses.settingContent} {
      padding: 4px 0px 16px 0px;
    }
  `,
);

const ActionBarToolbar = styled(Grid)``;

const ActionBarButtonsButton = styled('button')(
  ({ theme }) => css`
    ${theme.typography['label/medium/md']};
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 14px 10px;
    height: 48px;
    border: none;
    background: none;
    cursor: pointer;

    & > .kc-console-icon {
      font-size: 20px;
    }

    &:hover::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      border-top: ${theme.mixins.border('border/strong/default')};
    }

    &[data-active='true']::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      border-top: ${theme.mixins.border('border/strongest/default')};
    }

    ${theme.mixins.focusRing('inside')};
  `,
);

const ActionBarButtonsButtonLabel = styled('span')(
  ({ theme }) => css`
    @container action-bar (max-width: ${theme.breakpoints.values.sm}px) {
      display: none;
    }
  `,
);

const ActionBarPaginationContainer = styled(Grid)`
  min-height: 48px;
`;

const ActionBarDivider = styled((props: DividerProps) => (
  <Divider
    orientation="vertical"
    margin={8}
    {...props}
    className={clsx(actionBarClasses.toolbarDivider, props.className)}
  />
))`
  height: 20px;
`;

const RefetchDisconnectedDot = styled('span')(
  ({ theme }) => css`
    position: absolute;
    top: 8px;
    right: 7px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: ${theme.palette.common.white} solid 1px;
    background-color: #da030a;
    pointer-events: none;
  `,
);

const RefetchButtonContainer = styled('div')(
  ({ theme }) => css`
    position: relative;
    display: inline-flex;

    &
      > button:hover
      + ${RefetchDisconnectedDot},
      &
      > button:focus-visible
      + ${RefetchDisconnectedDot} {
      border-color: ${theme.color['actionSurface/ghost/hover']};
    }
  `,
);
